/* eslint-disable */
import React from 'react';
import { StandUpProvider, useStandUp } from 'standup/contexts/StandUpContext';
import StandUpBridge from 'standup/StandUpBridge';
import ManagerView from 'standup/components/ManagerView';
import EngineerView from 'standup/components/EngineerView';
import CreateTaskModal from 'standup/components/CreateTaskModal';
import TeamActionModal from 'standup/components/TeamActionModal';
import TeamSettingsModal from 'standup/components/TeamSettingsModal';
import TeamMembersModal from 'standup/components/TeamMembersModal';
import SettingsModal from 'standup/components/SettingsModal';
import TaskDetailModal from 'standup/components/TaskDetailModal';
import Toast from 'standup/components/Toast';
import InvitationNotification from 'standup/components/InvitationNotification';
import { Settings } from 'lucide-react';
import HeaderButtons from 'components/Button/HeaderButtons';
import { MdOutlineAddCircleOutline } from 'react-icons/md';

const StandUpIntegratedTasks = () => {
    const { 
        view, setView, currentRole, loading, 
        openModal, openSettingsModal, openTeamModal 
    } = useStandUp();

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-500 font-medium">Synchronizing with StandUp...</span>
            </div>
        );
    }

    /*
     * NOTE (scrollbars): this used to be h-[calc(100vh-50px)]. The admin shell
     * already gives every page a scroll container of exactly
     * h-[calc(100vh-70px)] (SideBar wraps <Outlet /> in
     * "flex-1 min-h-0 overflow-auto"), so asking for 100vh-50px made this page
     * taller than the area it was handed. The shell scrolled to fit it AND
     * EngineerView/ManagerView scrolled their own "h-full overflow-y-auto"
     * body — two vertical scrollbars side by side. h-full fills the given area
     * exactly, leaving the view's own scrollbar as the only one.
     */
    return (
        <div className="pl-2 relative h-full flex flex-col">
            {/* Action Bar */}
            <div className="my-1 flex flex-wrap items-center justify-between gap-4 shrink-0 px-1">
                <div className="flex items-center gap-2 p-1 dark:bg-gray-800">
                    {currentRole === 'manager' && (
                        <>
                            <HeaderButtons
                                type="button"
                                variant={view === 'manager' ? 'selected' : 'inactive'}
                                onClick={() => setView('manager')}
                            >
                                Manager Dashboard
                            </HeaderButtons>

                            <HeaderButtons
                                type="button"
                                variant={view === 'engineer' ? 'selected' : 'inactive'}
                                onClick={() => setView('engineer')}
                            >
                                My Focus
                            </HeaderButtons>
                        </>
                    )}

                    {currentRole !== 'manager' && (
                        <HeaderButtons
                            type="button"
                            variant="selected"
                            className="cursor-default"
                        >
                            My Focus
                        </HeaderButtons>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => openModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-all text-sm font-bold"
                    >
                        <MdOutlineAddCircleOutline size={18} style={{ marginRight: 5 }} />
                        Add Task
                    </button>
                    <button 
                        onClick={() => openSettingsModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-sm font-bold"
                    >
                        <Settings size={18} />
                        Settings
                    </button>
                </div>
            </div>

            {/* flex-1 min-h-0 takes the space left under the action bar and lets
                the view's own overflow-y-auto body shrink and scroll; h-full here
                would have stacked a full-height body under the bar and overflowed
                the page again */}
            <div className="flex-1 min-h-0">
                {view === 'manager' && currentRole === 'manager' ? <ManagerView /> : <EngineerView />}
            </div>

            {/* Global Modals */}
            <CreateTaskModal />
            <TeamActionModal />
            <TeamSettingsModal />
            <TeamMembersModal />
            <SettingsModal />
            <TaskDetailModal />
            <Toast />
            <InvitationNotification />
        </div>
    );
};

const TasksPage = () => {

    return (
        // h-full so the height chain from the shell reaches the page root above;
        // overflow-x-auto is kept for the manager board's wide content
        <div className="h-full overflow-x-auto">
            <StandUpBridge>
                <StandUpProvider>
                    {/* <BreadCrumb BreadCrumbList={BreadCrumbList} /> */}
                    <StandUpIntegratedTasks />
                </StandUpProvider>
            </StandUpBridge>
        </div>
    );
};

export default TasksPage;
